import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import { getGeneralSchedules } from '../Calendar/api/generalScheduleApi';
import { canViewAllScheduledCalendar, filterScheduledCalendar } from './scheduledCalendarVisibility';
import AuditPipelineOverview from '../../components/audit/AuditPipelineOverview';
import AuditPipelineInsights from '../../components/audit/AuditPipelineInsights';
import CertificateLifecycleHealthCard from '../../components/audit/CertificateLifecycleHealthCard';
import { filterExcludedAuditTasks } from '../../utils/auditTaskUtils';
import { buildAuditPipelineStats, filterMyOpenAuditTasks, isCompletedAuditTask, resolveAuditTaskAwareness } from '../../utils/auditPipelineUtils';
import { hasBroaderAuditVisibility, isSalesDashboardUser, resolveDashboardTier } from './dashboardRoleTiers';
import { profileNeedsCertificateHealth, profileNeedsClientMonitoring, profileNeedsUtilization, resolveDashboardProfile } from './dashboardRoleProfiles';
import { flattenSalesProjectAuditSchedules, includeSalesOwnedAuditSchedules, mergeUniqueSchedules } from './overviewScheduleUtils';
import { groupTasksByClient } from './clientAuditMonitoringUtils';
import { filterAuditManagementTasks } from '../../components/audit/auditManagementAnalytics';
import { paginate } from '../../utils/paginationUtils';
import AttentionNeededSection from './sections/AttentionNeededSection';
import ClientAuditMonitoringSection from './sections/ClientAuditMonitoringSection';
import AuditorUtilizationSummary from './sections/AuditorUtilizationSummary';
import OverviewScheduleSection from './sections/OverviewScheduleSection';
import { resolveServiceProgramInsightLabel, resolveStandardInsightLabel } from './sections/MonitoringInsights';
import DashboardViewAllButton from '../../components/audit/DashboardViewAllButton';
import { getDefaultMonthRange } from '../AuditorUtilization/auditorUtilizationUtils';
import {
  ClipboardList, Calendar, AlertCircle,
  RefreshCw, Search,
  ExternalLink, Users, GraduationCap, UserCheck, UserX, Activity,
  ListTodo, ArrowRight, ArrowLeft
} from 'lucide-react';

// ── Skeleton helpers ───────────────────────────────────────────────────────
const Skeleton = ({ w = 'col-6', h = 20 }) => (
  <span className={`placeholder d-block rounded ${w}`} style={{ height: h }} />
);

// ── Task Widget ────────────────────────────────────────────────────────────
// 5 rows keeps this card the same height as Client Audit Monitoring beside it, so the
// two-up row stays balanced instead of one half running long.
const TASK_PAGE_SIZE = 5;

const TaskWidget = ({ tasks, loading, navigate, stageMap, isManagementTier = false, currentUser, title = 'My Tasks' }) => {
  const [taskSearch, setTaskSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  // Management roles (admin/director/manager/head/BOD) default to "My Tasks" too — they can
  // switch to "All Tasks" since they're the only ones this backend endpoint returns company-wide
  // data for. Everyone else already only ever receives their own tasks from the API.
  const [taskViewMode, setTaskViewMode] = useState('mine');

  const myTasks = useMemo(() => {
    return filterMyOpenAuditTasks(tasks, currentUser);
  }, [tasks, currentUser]);
  const scopedTasks = isManagementTier && taskViewMode === 'all' ? tasks : myTasks;

  const visibleTasks = useMemo(() => {
    let list = [...scopedTasks];
    if (statusFilter !== 'ALL') {
      list = list.filter((task) => String(task?.status || '').toUpperCase() === statusFilter);
    }
    if (taskSearch.trim()) {
      const q = taskSearch.toLowerCase();
      list = list.filter(t =>
        (t.companyName || '').toLowerCase().includes(q) ||
        (t.projectCode || '').toLowerCase().includes(q) ||
        (t.taskType || t.taskTypeCode || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [scopedTasks, taskSearch, statusFilter]);

  useEffect(() => { setPage(1); }, [taskSearch, statusFilter, scopedTasks]);

  const {
    pageRows: pagedTasks, currentPage, totalPages, pageStart, rangeEnd,
  } = paginate(visibleTasks, page, TASK_PAGE_SIZE);
  const statusOptions = useMemo(() => Array.from(new Set(
    scopedTasks.map((task) => String(task?.status || '').toUpperCase()).filter(Boolean),
  )).sort(), [scopedTasks]);

  return (
    <div className="card border-0 shadow-sm w-100">
      <div className="card-header bg-transparent d-flex flex-wrap align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 me-auto">
          <div className="rounded-2 d-flex align-items-center justify-content-center"
            style={{ width: 32, height: 32, background: 'rgba(26,118,126,.1)' }}>
            <ListTodo size={16} color="#1a767e" />
          </div>
          <h3 className="card-title fw-semibold mb-0">{title}</h3>
          {!loading && (
            <span className="badge" style={{ background: 'rgba(26,118,126,.12)', color: '#1a767e', fontSize: '0.72rem' }}>
              {visibleTasks.length}
            </span>
          )}
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {isManagementTier && (
            <div className="btn-group btn-group-sm" role="group" aria-label="Audit task scope">
              <button
                type="button"
                className={`btn ${taskViewMode === 'mine' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setTaskViewMode('mine')}
                aria-pressed={taskViewMode === 'mine'}
              >
                My Tasks
              </button>
              <button
                type="button"
                className={`btn ${taskViewMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setTaskViewMode('all')}
                aria-pressed={taskViewMode === 'all'}
              >
                All Tasks
              </button>
            </div>
          )}
          <select
            className="form-select form-select-sm"
            style={{ width: 142 }}
            aria-label="Filter audit tasks by status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">All statuses</option>
            {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <div className="input-group input-group-sm" style={{ width: 180 }}>
            <span className="input-group-text border-0 bg-transparent"><Search size={13} className="text-muted" /></span>
            <input type="text" className="form-control border-0"
              style={{ background: 'rgba(0,0,0,.04)', borderRadius: '0 8px 8px 0' }}
              placeholder="Company / project…"
              value={taskSearch} onChange={e => setTaskSearch(e.target.value)} />
          </div>
          <DashboardViewAllButton onClick={() => navigate('/task?tab=audit-task')} />
        </div>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table mb-0">
            <thead style={{ background: 'rgba(0,0,0,.02)' }}>
              <tr>
                <th style={{ width: 48 }}>No.</th>
                <th>Project / Client</th>
                <th>Standard / Cycle</th>
                <th>Stage</th>
                <th>Assigned / Created</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 }).map((_, c) => (
                      <td key={c}><div className="placeholder-glow"><span className="placeholder col-8 rounded" style={{ height: 14 }} /></div></td>
                    ))}
                  </tr>
                ))
                : visibleTasks.length === 0
                ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      <ListTodo size={36} className="mb-2 opacity-25 d-block mx-auto" />
                      {tasks.length === 0
                        ? 'No audit tasks assigned.'
                        : 'No tasks match the current filter.'}
                    </td>
                  </tr>
                )
                : pagedTasks.map((t, i) => {
                  const taskAwareness = resolveAuditTaskAwareness(t);
                  return (
                    <tr
                      key={t.id || i}
                      role="link"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                      title="Open audit process"
                      onClick={() => navigate(`/audit-process/${t.projectId}`)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') navigate(`/audit-process/${t.projectId}`);
                      }}
                    >
                      <td className="text-muted small">{pageStart + i + 1}</td>
                      <td><div className="fw-semibold">{t.companyName || '—'}</div><span className="fw-mono text-muted small">{t.projectCode || `#${t.projectId}`}</span></td>
                      <td>
                        <div className="fw-medium small">{resolveStandardInsightLabel(t) || '—'}</div>
                        <div className="text-muted small">{resolveServiceProgramInsightLabel(t) || t.auditCycle || 'Cycle not specified'}</div>
                      </td>
                      <td style={{ minWidth: 270 }}>
                        <span className="badge" style={{ background: 'rgba(26,118,126,.12)', color: '#1a767e', fontSize: '0.68rem' }}>{stageMap?.[String(t.projectId)] || 'Not started'}</span>
                        {taskAwareness && (
                          <div
                            className="mt-2 rounded-2 px-2 py-2"
                            aria-label={`Task awareness: ${taskAwareness.title || taskAwareness.description}`}
                            style={{
                              background: taskAwareness.isUrgent ? '#fff7ed' : '#f2f8f8',
                              borderLeft: `3px solid ${taskAwareness.isUrgent ? '#ea580c' : '#1a767e'}`,
                              color: taskAwareness.isUrgent ? '#9a3412' : '#164e54',
                            }}
                          >
                            {taskAwareness.title && (
                              <div className="fw-semibold" style={{ fontSize: '0.72rem', lineHeight: 1.3 }}>
                                {taskAwareness.title}
                              </div>
                            )}
                            {taskAwareness.description && (
                              <div
                                className="mt-1"
                                title={taskAwareness.description}
                                style={{
                                  color: taskAwareness.isUrgent ? '#c2410c' : '#54757a',
                                  display: '-webkit-box',
                                  fontSize: '0.68rem',
                                  lineHeight: 1.35,
                                  overflow: 'hidden',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 2,
                                }}
                              >
                                {taskAwareness.description}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {t.assignedUsername && t.assignedUsername !== '—' && (
                            <div className="avatar avatar-xs rounded-circle d-flex align-items-center justify-content-center text-white"
                              style={{ width: 22, height: 22, fontSize: '0.58rem', background: '#1a767e', flexShrink: 0 }}>
                              {t.assignedUsername.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="text-muted small">{t.assignedUsername || '—'}</span>
                        </div>
                        <div className="text-muted small mt-1">Created {formatTaskCreateDate(t.createdAt)}</div>
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        {!loading && visibleTasks.length > 0 && (
          <div className="card-footer bg-transparent text-muted small d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span>
              Showing <strong className="text-body">{pageStart + 1}-{rangeEnd}</strong> of <strong className="text-body">{visibleTasks.length}</strong> tasks
            </span>
            <div className="d-flex align-items-center gap-1" style={{ background: 'rgba(0,0,0,.03)', borderRadius: 24, padding: 4 }}>
              <button
                className="btn btn-sm d-flex align-items-center justify-content-center"
                style={{
                  width: 30, height: 30, borderRadius: '50%', border: 'none', padding: 0,
                  background: currentPage <= 1 ? 'transparent' : '#fff',
                  color: currentPage <= 1 ? '#c1c7d0' : '#1a767e',
                  boxShadow: currentPage <= 1 ? 'none' : '0 1px 3px rgba(0,0,0,.12)',
                  cursor: currentPage <= 1 ? 'default' : 'pointer',
                }}
                disabled={currentPage <= 1}
                aria-label="Previous page"
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                ‹
              </button>
              <span className="px-2 fw-semibold" style={{ fontSize: '0.78rem', color: '#1a767e' }}>
                {currentPage} <span className="text-muted fw-normal">/ {totalPages}</span>
              </span>
              <button
                className="btn btn-sm d-flex align-items-center justify-content-center"
                style={{
                  width: 30, height: 30, borderRadius: '50%', border: 'none', padding: 0,
                  background: currentPage >= totalPages ? 'transparent' : '#fff',
                  color: currentPage >= totalPages ? '#c1c7d0' : '#1a767e',
                  boxShadow: currentPage >= totalPages ? 'none' : '0 1px 3px rgba(0,0,0,.12)',
                  cursor: currentPage >= totalPages ? 'default' : 'pointer',
                }}
                disabled={currentPage >= totalPages}
                aria-label="Next page"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Task helpers ───────────────────────────────────────────────────────────
const formatTaskCreateDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const flattenAuditCalendarOverview = (payload = {}) => (Array.isArray(payload?.projects) ? payload.projects : []).flatMap((project) =>
  (Array.isArray(project?.schedules) ? project.schedules : []).flatMap((scope) =>
    (Array.isArray(scope?.auditSchedules) ? scope.auditSchedules : []).flatMap((schedule, index) => {
      const dates = Array.isArray(schedule?.mandayDetails) && schedule.mandayDetails.length > 0
        ? schedule.mandayDetails.map((row) => String(row?.date || '').slice(0, 10)).filter(Boolean)
        : expandDateRange(schedule?.startDate, schedule?.endDate);
      return dates.map((date, dateIndex) => ({ ...schedule, id: `audit-${schedule?.id || index}-${dateIndex}`, startDate: date, projectId: project?.projectId, projectCode: project?.projectCode, companyName: project?.companyName, projectName: project?.projectName, scheduleKind: 'audit', typeLabel: 'Audit' }));
    })
  )
);

const GENERAL_SCHEDULE_META = {
  meeting: { label: 'Meeting', color: '#ed8936', bg: '#fef3e2', border: '#f7c686' },
  'sales-visit': { label: 'Sales Visit', color: '#48bb78', bg: '#e6ffed', border: '#9fe0b7' },
  'business-trip': { label: 'Business Trip / Travel', color: '#38a169', bg: '#e0f2e9', border: '#95d7b6' },
  event: { label: 'Event', color: '#3182ce', bg: '#e3f2fd', border: '#9dcef1' },
};

const expandDateRange = (startValue, endValue) => {
  const start = String(startValue || '').slice(0, 10);
  const end = String(endValue || startValue || '').slice(0, 10);
  if (!start || !end) return [];
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime()) || cursor > last) return [];
  const dates = [];
  while (cursor <= last) {
    dates.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const flattenGeneralSchedules = (schedules = []) => schedules.flatMap((schedule) => {
  const type = String(schedule?.type || 'event').toLowerCase();
  const meta = GENERAL_SCHEDULE_META[type] || { label: type || 'Schedule', color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1' };
  return expandDateRange(schedule?.startDate, schedule?.endDate).map((date) => ({
    ...schedule,
    id: `general-${schedule?.id || `${type}-${date}`}-${date}`,
    startDate: date,
    scheduleKind: 'general',
    typeLabel: meta.label,
    visual: meta,
  }));
});

const getCalendarScheduleMeta = (schedule) => schedule?.scheduleKind === 'audit'
  ? { label: 'Audit', color: '#b794f6', bg: '#e9e3f5', border: '#cfb6f4' }
  : schedule?.visual || GENERAL_SCHEDULE_META[schedule?.type] || { label: 'Schedule', color: '#475569', bg: '#f1f5f9', border: '#cbd5e1' };

const ScheduledCalendarFilters = ({ canViewAll, scope, scheduleType, onScopeChange, onScheduleTypeChange }) => (
  <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
    <div className="btn-group btn-group-sm" role="group" aria-label="Calendar schedule scope">
      <button type="button" className={`btn ${scope === 'own' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => onScopeChange('own')}>My schedules</button>
      {canViewAll && <button type="button" className={`btn ${scope === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => onScopeChange('all')}>All schedules</button>}
    </div>
    <div className="btn-group btn-group-sm" role="group" aria-label="Calendar schedule type">
      {[['all', 'All types'], ['audit', 'Audit'], ['non-audit', 'Non-audit']].map(([value, label]) => <button key={value} type="button" className={`btn ${scheduleType === value ? 'btn-success' : 'btn-outline-success'}`} onClick={() => onScheduleTypeChange(value)}>{label}</button>)}
    </div>
  </div>
);

const ScheduledAuditCalendar = ({ schedules, navigate }) => {
  const [monthCursor, setMonthCursor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedDaySchedules, setSelectedDaySchedules] = useState(null);
  const monthStart = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
  const firstWeekday = monthStart.getDay();
  const daysInMonth = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0).getDate();
  const byDay = schedules.reduce((result, schedule) => {
    const date = String(schedule?.startDate || '').slice(0, 10);
    if (date) (result[date] ||= []).push(schedule);
    return result;
  }, {});
  const cells = Array.from({ length: Math.ceil((firstWeekday + daysInMonth) / 7) * 7 }, (_, index) => index - firstWeekday + 1);
  const monthPrefix = `${monthCursor.getFullYear()}-${String(monthCursor.getMonth() + 1).padStart(2, '0')}`;
  const monthlySchedules = schedules.filter((schedule) => String(schedule?.startDate || '').startsWith(monthPrefix));
  const monthlyAudits = monthlySchedules.filter((schedule) => schedule?.scheduleKind === 'audit');
  const monthlyGeneralSchedules = monthlySchedules.filter((schedule) => schedule?.scheduleKind === 'general');
  const assignedPeople = new Set(monthlySchedules.flatMap((schedule) => schedule?.scheduleKind === 'audit'
    ? [schedule?.employeeId || schedule?.employeeName].filter(Boolean)
    : (Array.isArray(schedule?.assignees) ? schedule.assignees.map((assignee) => assignee?.userId || assignee?.fullName).filter(Boolean) : []))).size;
  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 };
  return <>
    <div className="d-flex align-items-center justify-content-between mb-4">
      <button
        type="button"
        className="btn btn-sm d-flex align-items-center justify-content-center"
        aria-label="Previous month"
        style={{
          width: 34, height: 34, padding: 0, borderRadius: '50%',
          border: '1px solid #d7e4e6', color: '#1a767e', background: '#fff',
        }}
        onMouseEnter={(event) => { event.currentTarget.style.background = '#1a767e'; event.currentTarget.style.color = '#fff'; event.currentTarget.style.borderColor = '#1a767e'; }}
        onMouseLeave={(event) => { event.currentTarget.style.background = '#fff'; event.currentTarget.style.color = '#1a767e'; event.currentTarget.style.borderColor = '#d7e4e6'; }}
        onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
      ><ArrowLeft size={16} /></button>
      <div className="text-center">
        <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
          <select
            className="form-select form-select-sm fw-semibold"
            style={{
              width: 'auto', borderRadius: 8, borderColor: '#d7e4e6', color: '#0d3d42',
              boxShadow: 'none', cursor: 'pointer',
            }}
            aria-label="Select month"
            value={monthCursor.getMonth()}
            onChange={(event) => setMonthCursor(new Date(monthCursor.getFullYear(), Number(event.target.value), 1))}
          >
            {Array.from({ length: 12 }, (_, index) => <option key={index} value={index}>{new Date(2000, index, 1).toLocaleDateString('en-US', { month: 'long' })}</option>)}
          </select>
          <select
            className="form-select form-select-sm fw-semibold"
            style={{
              width: 'auto', borderRadius: 8, borderColor: '#d7e4e6', color: '#0d3d42',
              boxShadow: 'none', cursor: 'pointer',
            }}
            aria-label="Select year"
            value={monthCursor.getFullYear()}
            onChange={(event) => setMonthCursor(new Date(Number(event.target.value), monthCursor.getMonth(), 1))}
          >
            {Array.from({ length: 11 }, (_, index) => new Date().getFullYear() - 5 + index).map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </div>
        <span className="text-muted small">Audit and operational schedules in one calendar</span>
      </div>
      <button
        type="button"
        className="btn btn-sm d-flex align-items-center justify-content-center"
        aria-label="Next month"
        style={{
          width: 34, height: 34, padding: 0, borderRadius: '50%',
          border: '1px solid #d7e4e6', color: '#1a767e', background: '#fff',
        }}
        onMouseEnter={(event) => { event.currentTarget.style.background = '#1a767e'; event.currentTarget.style.color = '#fff'; event.currentTarget.style.borderColor = '#1a767e'; }}
        onMouseLeave={(event) => { event.currentTarget.style.background = '#fff'; event.currentTarget.style.color = '#1a767e'; event.currentTarget.style.borderColor = '#d7e4e6'; }}
        onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
      ><ArrowRight size={16} /></button>
    </div>
    <div className="row g-2 mb-4"><div className="col-6 col-md-3"><div className="border rounded-3 p-3 h-100" style={{ borderTop: '3px solid #1a767e' }}><div className="text-muted small">All schedules</div><strong className="fs-4">{monthlySchedules.length}</strong></div></div><div className="col-6 col-md-3"><div className="border rounded-3 p-3 h-100" style={{ borderTop: '3px solid #0ca678' }}><div className="text-muted small">Audit schedules</div><strong className="fs-4">{monthlyAudits.length}</strong></div></div><div className="col-6 col-md-3"><div className="border rounded-3 p-3 h-100" style={{ borderTop: '3px solid #4263eb' }}><div className="text-muted small">Operational schedules</div><strong className="fs-4">{monthlyGeneralSchedules.length}</strong></div></div><div className="col-6 col-md-3"><div className="border rounded-3 p-3 h-100" style={{ borderTop: '3px solid #7950f2' }}><div className="text-muted small">People assigned</div><strong className="fs-4">{assignedPeople}</strong></div></div></div>
    <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 760 }}>
    <div style={{ ...gridStyle, marginBottom: 6 }}>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day) => <div key={day} className="text-center small text-uppercase fw-semibold text-muted" style={{ letterSpacing: '.06em' }}>{day}</div>)}</div>
    <div style={gridStyle}>
      {cells.map((day, index) => {
        if (day < 1 || day > daysInMonth) return <div key={index} className="rounded-3" style={{ minHeight: 112, background: '#f8fafc', border: '1px solid #edf0f3' }} />;
        const date = `${monthCursor.getFullYear()}-${String(monthCursor.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const events = byDay[date] || [];
        return <div key={date} className="rounded-3 p-2" style={{ minHeight: 112, background: events.length ? '#fbfffd' : '#fff', border: `1px solid ${events.length ? '#b7ead8' : '#e6eaef'}` }}><div className="small fw-semibold text-muted mb-1">{day}</div>{events.slice(0, 3).map((schedule) => { const meta = getCalendarScheduleMeta(schedule); return <button key={schedule.id} type="button" className="btn btn-sm w-100 text-truncate text-start px-2 py-1 mb-1" style={{ fontSize: '0.68rem', borderRadius: 6, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, borderLeft: `3px solid ${meta.color}` }} title={`${meta.label} · ${schedule.title || schedule.companyName || schedule.projectCode || 'Schedule'}`} onClick={() => setSelectedSchedule(schedule)}><span className="me-1">●</span>{schedule.title || schedule.companyName || schedule.projectCode || meta.label}</button>; })}{events.length > 3 && <button type="button" className="btn btn-link btn-sm text-success ps-1 py-0" style={{ fontSize: '0.72rem' }} onClick={() => setSelectedDaySchedules({ date, events })}>+{events.length - 3} more</button>}</div>;
      })}
    </div></div></div>
    {selectedDaySchedules && <div onMouseDown={() => setSelectedDaySchedules(null)} style={{ position: 'fixed', inset: 0, zIndex: 1045, background: 'rgba(7, 35, 43, .45)', display: 'grid', placeItems: 'center', padding: 20 }} role="dialog" aria-modal="true" aria-label="Schedules for selected date"><div onMouseDown={(event) => event.stopPropagation()} style={{ width: 'min(620px, 100%)', maxHeight: 'min(680px, calc(100vh - 40px))', overflow: 'auto', borderRadius: 12, background: '#fff', boxShadow: '0 24px 70px rgba(0,0,0,.28)' }}><div className="d-flex align-items-start justify-content-between gap-3 border-bottom" style={{ padding: '20px 22px' }}><div><div className="text-uppercase small fw-semibold" style={{ color: '#1a767e', letterSpacing: '.06em' }}>Scheduled calendar</div><h3 className="h5 mb-0 mt-1">{new Date(`${selectedDaySchedules.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</h3></div><button type="button" className="btn btn-sm btn-light rounded-circle" style={{ width: 32, height: 32 }} onClick={() => setSelectedDaySchedules(null)} aria-label="Close schedule list">×</button></div><div className="p-3">{selectedDaySchedules.events.map((schedule) => { const meta = getCalendarScheduleMeta(schedule); return <button key={schedule.id} type="button" className="btn w-100 text-start mb-2 p-3" style={{ border: `1px solid ${meta.border}`, borderLeft: `4px solid ${meta.color}`, background: meta.bg, borderRadius: 8 }} onClick={() => { setSelectedDaySchedules(null); setSelectedSchedule(schedule); }}><div className="d-flex align-items-start justify-content-between gap-3"><div className="text-truncate"><div className="fw-semibold text-truncate">{schedule.title || schedule.companyName || schedule.projectCode || meta.label}</div><div className="small mt-1" style={{ color: meta.color }}>{meta.label} · {schedule.scheduleKind === 'audit' ? schedule.employeeName || 'Not assigned' : (schedule.assignees || []).map((assignee) => assignee?.fullName).filter(Boolean).join(', ') || 'Not assigned'}</div></div><ArrowRight size={16} style={{ color: meta.color, flex: '0 0 auto' }} /></div></button>; })}</div></div></div>}
    {selectedSchedule && (() => { const isAudit = selectedSchedule.scheduleKind === 'audit'; const meta = getCalendarScheduleMeta(selectedSchedule); const assigneeNames = isAudit ? (selectedSchedule.employeeName || 'Not assigned') : (Array.isArray(selectedSchedule.assignees) ? selectedSchedule.assignees.map((assignee) => assignee?.fullName).filter(Boolean).join(', ') || 'Not assigned' : 'Not assigned'); const details = isAudit ? [{ label: 'Schedule date', value: selectedSchedule.startDate, accent: '#0ca678' }, { label: 'Audit type', value: selectedSchedule.auditType || 'On-site', accent: '#4263eb' }, { label: 'Assigned auditor', value: assigneeNames, accent: '#7950f2' }, { label: 'Schedule status', value: selectedSchedule.scheduleStatus || 'Booked', accent: '#f59f00' }] : [{ label: 'Schedule date', value: selectedSchedule.startDate, accent: meta.color }, { label: 'Schedule type', value: meta.label, accent: '#4263eb' }, { label: 'Assignees', value: assigneeNames, accent: '#7950f2' }, { label: 'Location / Site', value: selectedSchedule.location || 'Not specified', accent: '#f59f00' }]; return <div onMouseDown={() => setSelectedSchedule(null)} style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(7, 35, 43, .55)', backdropFilter: 'blur(4px)', display: 'grid', placeItems: 'center', padding: 20 }} role="dialog" aria-modal="true"><div onMouseDown={(event) => event.stopPropagation()} style={{ width: 'min(560px, 100%)', overflow: 'hidden', borderRadius: 18, background: '#fff', boxShadow: '0 24px 70px rgba(0,0,0,.28)' }}><div style={{ padding: '22px 24px', color: '#fff', background: 'linear-gradient(135deg, #0d5962, #1a767e 65%, #2aa5a8)' }}><div className="d-flex justify-content-between align-items-start gap-3"><div><div className="text-uppercase small fw-semibold opacity-75" style={{ letterSpacing: '.09em' }}>{meta.label}</div><h3 className="h4 mt-1 mb-0 text-white">{selectedSchedule.title || selectedSchedule.companyName || selectedSchedule.projectName || selectedSchedule.projectCode || meta.label}</h3></div><button type="button" className="btn btn-sm btn-light rounded-circle" style={{ width: 32, height: 32 }} onClick={() => setSelectedSchedule(null)} aria-label="Close">×</button></div><div className="mt-3"><span className="badge rounded-pill" style={{ background: 'rgba(255,255,255,.18)', color: '#fff', border: '1px solid rgba(255,255,255,.3)' }}>{isAudit ? selectedSchedule.scheduleStatus || 'Booked' : meta.label}</span><span className="ms-2 small opacity-75">{selectedSchedule.companyName || selectedSchedule.projectCode || selectedSchedule.notes || ''}</span></div></div><div style={{ padding: 24 }}><div className="row g-3">{details.map((item) => <div className="col-6" key={item.label}><div className="rounded-3 p-3 h-100" style={{ background: `${item.accent}0d`, borderLeft: `3px solid ${item.accent}` }}><div className="text-muted small">{item.label}</div><div className="fw-semibold mt-1 text-truncate" title={item.value}>{item.value}</div></div></div>)}</div>{!isAudit && selectedSchedule.notes && <div className="mt-3 rounded-3 p-3" style={{ background: '#f8fafb' }}><div className="text-muted small">Notes</div><div className="small mt-1">{selectedSchedule.notes}</div></div>}</div><div className="d-flex justify-content-end gap-2 border-top" style={{ padding: '16px 24px', background: '#f8fafb' }}><button type="button" className="btn btn-light" onClick={() => setSelectedSchedule(null)}>Close</button>{isAudit && selectedSchedule.projectId && <button type="button" className="btn text-white" style={{ background: '#1a767e' }} onClick={() => navigate(`/audit-process/${selectedSchedule.projectId}`)}>View Audit Process <ArrowRight size={15} className="ms-1" /></button>}</div></div></div>; })()}
  </>;
};

// ── Main Component ─────────────────────────────────────────────────────────
const AuditDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManagementTier = resolveDashboardTier(user) === 'management';
  const isSalesPortfolioMonitoring = isSalesDashboardUser(user) && !isManagementTier;

  const [tasks, setTasks]         = useState([]);
  const [isBroaderVisibility, setIsBroaderVisibility] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]         = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedPipelineStage, setSelectedPipelineStage] = useState(null);
  const [portfolioFilter, setPortfolioFilter] = useState(null);
  const [monitoringInsightFilter, setMonitoringInsightFilter] = useState(null);

  // Certificate + Competency stats
  const [certStats, setCertStats] = useState(null);
  const [compStats, setCompStats] = useState(null);
  const [qualBreakdown, setQualBreakdown] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [scheduledAudits, setScheduledAudits] = useState([]);
  const [auditorUtilization, setAuditorUtilization] = useState([]);
  const [attentionNeeded, setAttentionNeeded] = useState(null);
  const canViewAllCalendarSchedules = canViewAllScheduledCalendar(user);
  const [scheduledCalendarScope, setScheduledCalendarScope] = useState(() => (
    canViewAllScheduledCalendar(user) ? 'all' : 'own'
  ));
  const [scheduledCalendarType, setScheduledCalendarType] = useState('all');
  useEffect(() => {
    if (!canViewAllCalendarSchedules) setScheduledCalendarScope('own');
  }, [canViewAllCalendarSchedules]);
  const visibleScheduledAudits = useMemo(() => (
    filterScheduledCalendar(scheduledAudits, user, scheduledCalendarScope, scheduledCalendarType)
  ), [scheduledAudits, user, scheduledCalendarScope, scheduledCalendarType]);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setStatsLoading(true);
    setError(null);
    const calendarScope = scheduledCalendarScope === 'all' && canViewAllCalendarSchedules ? 'ALL' : 'OWN';
    try {
      const [tasksRes, statsRes] = await Promise.all([
        api.get(isSalesPortfolioMonitoring ? '/audit/tasks?monitoring=PROJECT_OWNER' : '/audit/tasks'),
        api.get('/audit/dashboard/cert-competency-stats').catch(() => ({ data: null })),
      ]);
      let nextTasks = filterExcludedAuditTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
      // Technical Reviewer / Certification Authority / Certificate Issuer have no fixed CRM Role
      // (see dashboardRoleTiers.hasBroaderAuditVisibility) — only detectable from the own-tasks
      // response we just fetched. When detected, re-fetch with monitoring=INVOLVED so they can
      // monitor every task (any type) on the projects they hold a task in, not just their own row.
      let nextBroaderVisibility = false;
      if (!isManagementTier && !isSalesPortfolioMonitoring && hasBroaderAuditVisibility(nextTasks)) {
        const involvedRes = await api.get('/audit/tasks?monitoring=INVOLVED').catch(() => null);
        if (Array.isArray(involvedRes?.data)) {
          nextTasks = filterExcludedAuditTasks(involvedRes.data);
          nextBroaderVisibility = true;
        }
      }
      setTasks(nextTasks);
      setIsBroaderVisibility(nextBroaderVisibility);
      let salesOwnedAuditSchedules = [];
      if (isSalesPortfolioMonitoring) {
        const projects = Array.from(new Map(nextTasks.filter((task) => task?.projectId != null).map((task) => [String(task.projectId), task])).values());
        const scheduleResponses = await Promise.all(projects.map(async (project) => {
          const response = await api.get(`/audit/projects/${project.projectId}/schedules`).catch(() => ({ data: null }));
          return { projectId: project.projectId, projectCode: project.projectCode, companyName: project.companyName, payload: response.data };
        }));
        salesOwnedAuditSchedules = flattenSalesProjectAuditSchedules(scheduleResponses);
      }
      // Mirror the master Calendar: audit schedules plus every operational schedule.
      const [overviewRes, generalSchedules, utilizationRes, attentionRes] = await Promise.all([
        api.get('/audit/calendar/overview', { params: { scope: calendarScope } }).catch(() => ({ data: null })),
        getGeneralSchedules(calendarScope).catch(() => []),
        api.get('/audit/auditor-utilization', { params: getDefaultMonthRange() }).catch(() => ({ data: [] })),
        api.get('/audit/dashboard/attention-needed').catch(() => ({ data: null })),
      ]);
      setAuditorUtilization(Array.isArray(utilizationRes?.data) ? utilizationRes.data : []);
      setAttentionNeeded(attentionRes?.data || null);
      setScheduledAudits(mergeUniqueSchedules([
        ...flattenAuditCalendarOverview(overviewRes?.data),
        ...salesOwnedAuditSchedules,
        ...flattenGeneralSchedules(Array.isArray(generalSchedules) ? generalSchedules : []),
      ]));
      if (statsRes.data) {
        setCertStats(statsRes.data.certificates || null);
        setCompStats(statsRes.data.competencies || null);
        setQualBreakdown(statsRes.data.qualificationBreakdown || []);
      }
      setLastUpdated(new Date());
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load audit data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setStatsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [scheduledCalendarScope, canViewAllCalendarSchedules]);

  const { counts, total, stageMap } = useMemo(() => buildAuditPipelineStats(tasks), [tasks]);
  const livePipelineTasks = useMemo(
    () => filterAuditManagementTasks(tasks, portfolioFilter),
    [tasks, portfolioFilter],
  );
  const {
    counts: liveCounts,
    total: liveTotal,
    stageMap: liveStageMap,
  } = useMemo(() => buildAuditPipelineStats(livePipelineTasks), [livePipelineTasks]);
  const clientGroups = useMemo(() => groupTasksByClient(tasks, stageMap), [tasks, stageMap]);
  const clientMonitoringProjects = useMemo(() => clientGroups.flatMap((group) => group.projects || []), [clientGroups]);
  const selectedPipelineProjectIds = useMemo(() => new Set(
    Object.entries(isManagementTier ? liveStageMap : stageMap)
      .filter(([, stage]) => stage === selectedPipelineStage)
      .map(([projectId]) => projectId),
  ), [isManagementTier, liveStageMap, stageMap, selectedPipelineStage]);
  const focusedPipelineTasks = useMemo(() => (
    selectedPipelineStage
      ? (isManagementTier ? livePipelineTasks : tasks).filter((task) => selectedPipelineProjectIds.has(String(task?.projectId)))
      : (isManagementTier ? livePipelineTasks : tasks)
  ), [isManagementTier, livePipelineTasks, tasks, selectedPipelineStage, selectedPipelineProjectIds]);
  const focusedPipelineSchedules = useMemo(() => (
    selectedPipelineStage
      ? visibleScheduledAudits.filter((schedule) => selectedPipelineProjectIds.has(String(schedule?.projectId)))
      : visibleScheduledAudits
  ), [visibleScheduledAudits, selectedPipelineStage, selectedPipelineProjectIds]);

  const handlePipelineStageClick = (stage) => {
    setSelectedPipelineStage((current) => current === stage ? null : stage);
  };

  // Open (non-DONE) tasks for current user view
  const openTasks = useMemo(() => tasks.filter((task) => !isCompletedAuditTask(task?.status)), [tasks]);
  const dashboardProfile = useMemo(() => resolveDashboardProfile(user, tasks), [user, tasks]);
  const profileSchedules = useMemo(() => (
    dashboardProfile === 'sales'
      ? includeSalesOwnedAuditSchedules(visibleScheduledAudits, tasks, user?.username)
      : visibleScheduledAudits
  ), [dashboardProfile, visibleScheduledAudits, tasks, user?.username]);

  // ── Role-specific operational dashboard ───────────────────────────────
  if (!isManagementTier) {
    const profileLabel = {
      sales: 'Sales Audit Workspace', 'application-review': 'Application Review Workspace', scheduler: 'Scheduling Workspace', auditor: 'Auditor Workspace', 'technical-certification': 'Technical & Certification Workspace',
    }[dashboardProfile] || 'Audit Workspace';
    const showMonitoring = profileNeedsClientMonitoring(dashboardProfile);
    return (
      <>
        <div className="container-xl pb-4">
          <div className="rounded-3 mb-4 p-4 text-white position-relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg,#0d5962 0%,#1a767e 70%,#20919a 100%)' }}>
            <div className="row align-items-center position-relative">
              <div className="col">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <ListTodo size={20} className="opacity-75" />
                  <span className="text-white-50 small">Audit Platform</span>
                </div>
                <h1 className="mb-1 fw-bold text-white" style={{ fontSize: 'clamp(1.3rem,3vw,1.75rem)' }}>
                  {profileLabel}
                </h1>
                <p className="mb-0" style={{ opacity: .8, fontSize: '0.875rem' }}>
                  {loading ? 'Loading…' : `${openTasks.length} open tasks · ${total} active audit projects`}
                </p>
              </div>
              <div className="col-auto">
                <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                  onClick={() => fetchData(true)} disabled={refreshing}>
                  <RefreshCw size={14} className={refreshing ? 'spin-animation' : ''} />
                  <span className="d-none d-sm-inline">{refreshing ? 'Refreshing…' : 'Refresh'}</span>
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger d-flex align-items-center gap-2 mb-4" role="alert">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <TaskWidget tasks={tasks} loading={loading} navigate={navigate} stageMap={stageMap} currentUser={user} title="My Tasks" />
          <div className="mt-4"><OverviewScheduleSection schedules={profileSchedules} loading={loading} navigate={navigate} canViewAll={dashboardProfile === 'scheduler' && canViewAllCalendarSchedules} scope={scheduledCalendarScope} onScopeChange={setScheduledCalendarScope} /></div>
          {showMonitoring && <div className="mt-4"><ClientAuditMonitoringSection groups={clientGroups} loading={loading} navigate={navigate} title="My Client Audit Monitoring" insightRows={clientMonitoringProjects} insightFilter={monitoringInsightFilter} onInsightFilterChange={setMonitoringInsightFilter} /></div>}
          {dashboardProfile === 'application-review' && <AttentionNeededSection data={attentionNeeded} loading={statsLoading} navigate={navigate} />}
          {profileNeedsUtilization(dashboardProfile) && <AuditorUtilizationSummary rows={auditorUtilization} loading={statsLoading} navigate={navigate} />}
          {profileNeedsCertificateHealth(dashboardProfile) && <div className="mt-4"><CertificateLifecycleHealthCard stats={certStats} title="Certificate Health & Lifecycle Status" loading={statsLoading} onViewAll={() => navigate('/audit/certificate-monitoring')} /></div>}
        </div>
        <style>{`.spin-animation{animation:spin 1s linear infinite}@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </>
    );
  }

  return (
    <>
      <div className="container-xl pb-4">

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="rounded-3 mb-4 p-4 text-white position-relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#0d5962 0%,#1a767e 70%,#20919a 100%)' }}>
          <div className="position-absolute top-0 end-0 opacity-10"
            style={{ width: 240, height: 240, borderRadius: '50%', background: 'white', transform: 'translate(30%,-30%)' }} />
          <div className="row align-items-center position-relative">
            <div className="col">
              <div className="d-flex align-items-center gap-2 mb-1">
                <ClipboardList size={20} className="opacity-75" />
                <span className="text-white-50 small">Audit Platform</span>
              </div>
              <h1 className="mb-1 fw-bold text-white" style={{ fontSize: 'clamp(1.3rem,3vw,1.75rem)' }}>
                Audit Platform Dashboard
              </h1>
              <p className="mb-0" style={{ opacity: .8, fontSize: '0.875rem' }}>
                {loading ? 'Loading…' : `${total} total audit projects · ${total - (counts.Completed || 0)} active`}
              </p>
            </div>
            <div className="col-auto d-flex align-items-center gap-2">
              {lastUpdated && (
                <span className="text-white-50 small d-none d-md-inline">
                  {lastUpdated.toLocaleTimeString('id-ID', { hour:'2-digit', minute:'2-digit' })}
                </span>
              )}
              <button className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
                onClick={() => fetchData(true)} disabled={refreshing}>
                <RefreshCw size={14} className={refreshing ? 'spin-animation' : ''} />
                <span className="d-none d-sm-inline">{refreshing ? 'Refreshing…' : 'Refresh'}</span>
              </button>
              <button className="btn btn-sm btn-light d-flex align-items-center gap-1"
                onClick={() => navigate('/task?tab=audit-task')}>
                <ExternalLink size={14} />
                <span className="d-none d-sm-inline">Audit Process</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Error ──────────────────────────────────────────────────────── */}
        {error && (
          <div className="alert alert-danger d-flex align-items-center gap-2 mb-4" role="alert">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <div className="mb-4"><TaskWidget tasks={tasks} loading={loading} navigate={navigate} stageMap={stageMap} isManagementTier currentUser={user} title="Audit Tasks" /></div>
        <div className="mb-4"><OverviewScheduleSection schedules={visibleScheduledAudits} loading={loading} navigate={navigate} canViewAll={canViewAllCalendarSchedules} scope={scheduledCalendarScope} onScopeChange={setScheduledCalendarScope} /></div>

        <div className="mb-4"><ClientAuditMonitoringSection groups={clientGroups} loading={loading} navigate={navigate} title="Client Audit Monitoring" insightRows={clientMonitoringProjects} insightFilter={monitoringInsightFilter} onInsightFilterChange={setMonitoringInsightFilter} /></div>

        <div className="mb-4">
          <AuditPipelineOverview counts={liveCounts} total={liveTotal} loading={loading} onStageClick={handlePipelineStageClick} selectedStage={selectedPipelineStage}>
            <AuditPipelineInsights tasks={focusedPipelineTasks} schedules={focusedPipelineSchedules} stageCounts={liveCounts} stageTotal={liveTotal} selectedStage={selectedPipelineStage} onClearStage={() => setSelectedPipelineStage(null)} loading={loading} portfolioFilter={portfolioFilter} navigate={navigate} onPortfolioFilterChange={(nextFilter) => { setPortfolioFilter(nextFilter); setSelectedPipelineStage(null); }} />
          </AuditPipelineOverview>
        </div>

        <AttentionNeededSection data={attentionNeeded} loading={statsLoading} navigate={navigate} />

        {/* ── Certificate + Competency Intelligence ─────────────────────── */}
        <div className="row g-3 mb-4 align-items-start">
          <div className="col-12 col-lg-6">
            <CertificateLifecycleHealthCard
              stats={certStats}
              title="Certificate Health & Lifecycle Status"
              loading={statsLoading}
              onViewAll={() => navigate('/audit/certificate-monitoring')}
            />
          </div>

          {/* Competency + Qualification */}
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-0 pb-0 d-flex align-items-center gap-2">
                <div className="rounded-2 d-flex align-items-center justify-content-center"
                  style={{ width: 32, height: 32, background: 'rgba(121,80,242,.12)' }}>
                  <GraduationCap size={16} color="#7950f2" />
                </div>
                <h3 className="card-title fw-semibold mb-0">Competency Mapping</h3>
                <DashboardViewAllButton className="ms-auto" onClick={() => navigate('/audit/competency-management')} />
              </div>
              <div className="card-body">
                {statsLoading ? (
                  <div className="placeholder-glow d-flex flex-column gap-2">
                    {[1,2,3].map(i => <Skeleton key={i} w="col-12" h={20} />)}
                  </div>
                ) : compStats ? (
                  <>
                    {/* Competency summary pills */}
                    <div className="row g-2 mb-3">
                      {[
                        { label: 'Total Imported', value: compStats.total,     color: '#1a767e', icon: Users },
                        { label: 'Matched',        value: compStats.matched,   color: '#2fb344', icon: UserCheck },
                        { label: 'Auto-created',   value: compStats.created,   color: '#4263eb', icon: Activity },
                        { label: 'Unmatched',      value: compStats.unmatched, color: '#f59f00', icon: UserX },
                      ].map(({ label, value, color, icon: Icon }) => (
                        <div key={label} className="col-6">
                          <div className="d-flex align-items-center gap-2 rounded-2 p-2"
                            style={{ background: `${color}11`, border: `1px solid ${color}22` }}>
                            <Icon size={14} color={color} />
                            <div>
                              <div className="fw-bold small" style={{ color }}>{value}</div>
                              <div className="text-muted" style={{ fontSize: '0.68rem' }}>{label}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Qualification breakdown */}
                    {qualBreakdown.length > 0 && (
                      <div>
                        <div className="fw-semibold mb-2 d-flex align-items-center gap-1"
                          style={{ fontSize: '0.78rem', color: '#555' }}>
                          <GraduationCap size={13} className="text-muted" />
                          Personnel Qualification Distribution
                        </div>
                        <div className="d-flex flex-column gap-1">
                          {qualBreakdown.slice(0, 6).map(({ name, count }) => {
                            const maxCount = qualBreakdown[0]?.count || 1;
                            const pct = Math.round((count / maxCount) * 100);
                            return (
                              <div key={name} className="d-flex align-items-center gap-2">
                                <div className="text-muted text-truncate" style={{ fontSize: '0.72rem', width: 130, flexShrink: 0 }}
                                  title={name}>{name}</div>
                                <div className="flex-grow-1">
                                  <div className="progress" style={{ height: 5, borderRadius: 3 }}>
                                    <div className="progress-bar" style={{
                                      width: `${pct}%`, background: '#7950f2', borderRadius: 3, transition: 'width .6s ease'
                                    }} />
                                  </div>
                                </div>
                                <div className="fw-semibold" style={{ fontSize: '0.72rem', color: '#7950f2', width: 28, textAlign: 'right' }}>
                                  {count}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-muted text-center py-4" style={{ fontSize: '0.85rem' }}>
                    No competency data yet. Import from AuditQ to get started.
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3">
              <AuditorUtilizationSummary rows={auditorUtilization} loading={statsLoading} navigate={navigate} />
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .spin-animation { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .fw-mono { font-family: monospace; }
        .min-w-0 { min-width: 0; }
      `}</style>
    </>
  );
};

export default AuditDashboard;
